import { useEffect, useState } from "react";
import { Loader2, Mail, MapPin, Phone, Save } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { http, errMsg, STATUSES, statusOf, fmtDate, handoffLabel } from "../../lib/api";
import { inputCls } from "../request/Field";
import { PostalApproval } from "./PostalApproval";

const Photo = ({ path, i }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    http.get(`/admin/files/${path}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => setUrl(false));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [path]);
  if (url === false) return <div className="grid aspect-square place-items-center border border-white/10 text-xs text-slate-400">Unavailable</div>;
  if (!url) return <div className="aspect-square animate-pulse bg-white/5" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`detail-photo-${i}`} className="block aspect-square overflow-hidden border border-white/10">
      <img src={url} alt={`Damage ${i + 1}`} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
    </a>
  );
};

const Row = ({ icon: Icon, children }) => (
  <p className="flex items-start gap-3 text-sm"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{children}</p>
);

export const RepairDetail = ({ repair, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (repair) setForm({ status: repair.status, public_note: repair.public_note, internal_notes: repair.internal_notes, estimate: repair.estimate });
  }, [repair]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.patch(`/admin/repairs/${repair.ref}`, form);
      onSaved(data);
      toast.success(`${repair.ref} updated`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Sheet open={!!repair} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-lenis-prevent className="w-full overflow-y-auto border-white/10 bg-ink p-0 sm:max-w-xl" data-testid="repair-detail-sheet">
        {repair && (
          <div className="p-6 md:p-8">
            <SheetHeader className="text-left">
              <p className="font-mono text-sm text-ember">{repair.ref}</p>
              <SheetTitle className="font-display text-3xl font-extrabold">{repair.brand_model}</SheetTitle>
              <SheetDescription className="capitalize text-slate-400">{repair.device_type} · {handoffLabel(repair.handoff)} · {fmtDate(repair.created_at)}</SheetDescription>
            </SheetHeader>
            <div className="mt-8 space-y-3 border-y border-white/10 py-6">
              <p className="font-display text-xl font-bold">{repair.name}</p>
              <Row icon={Mail}><a href={`mailto:${repair.email}`} className="hover:text-ember">{repair.email}</a></Row>
              <Row icon={Phone}><a href={`tel:${repair.phone}`} className="hover:text-ember">{repair.phone}</a></Row>
              {repair.address && <Row icon={MapPin}>{repair.address}</Row>}
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">Prefers: {repair.preferred_contact}</p>
            </div>
            {repair.handoff === "post" && <PostalApproval repair={repair} onSaved={onSaved} />}
            <div className="mt-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{repair.kind === "build" ? "Build details" : "Problem"}</p>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{repair.problem}</p>
            </div>
            {repair.photos?.length > 0 && (
              <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {repair.photos.map((p, i) => <Photo key={p} path={p} i={i} />)}
              </div>
            )}
            <div className="mt-8 space-y-4 border-t border-white/10 pt-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Update</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s.key} type="button" data-testid={`detail-status-${s.key}`} onClick={() => set("status", s.key)}
                    className={`border px-3 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors ${form.status === s.key ? "text-ink" : "border-white/10 text-slate-300 hover:border-white/30"}`}
                    style={form.status === s.key ? { background: s.color, borderColor: s.color } : {}}>
                    {s.label}
                  </button>
                ))}
              </div>
              <textarea data-testid="detail-public-note" rows={2} className={`${inputCls} resize-none text-sm`} value={form.public_note || ""} onChange={(e) => set("public_note", e.target.value)} placeholder="Note visible to the customer on the tracker" />
              <input data-testid="detail-estimate" className={`${inputCls} text-sm`} value={form.estimate || ""} onChange={(e) => set("estimate", e.target.value)} placeholder="Estimate (visible to customer), e.g. £65 — screen replacement" />
              <textarea data-testid="detail-internal-notes" rows={3} className={`${inputCls} resize-none text-sm`} value={form.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} placeholder="Internal diagnostic notes (staff only)" />
              <button data-testid="detail-save-btn" onClick={save} disabled={busy} className="inline-flex w-full items-center justify-center gap-2 bg-ember px-6 py-4 font-semibold text-ink hover:bg-amber-400 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
              </button>
            </div>
            <div className="mt-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">History</p>
              <ul className="mt-3 space-y-2">
                {[...(repair.history || [])].reverse().map((h, i) => (
                  <li key={i} className="flex gap-3 text-sm"><span className="w-28 shrink-0 font-mono text-xs text-slate-400">{fmtDate(h.at)}</span><span style={{ color: statusOf(h.status).color }}>{statusOf(h.status).label}</span></li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
